// src/components/dashboard/RevenueChecker.jsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Skeleton,
  Avatar,
} from '@mui/material';
import { Home, Restaurant, Hotel, Build } from '@mui/icons-material';
import axios from 'axios';
import { API_GET_INVOICES_TOTAL_COUNT } from '../../Util/URLConstants';
import StorageFactory from '../../Util/StorageFactory';
import { SESSION_USER_COUNTRY_DATA_KEY } from '../../Util/VMConstants';
import MUITypography from '../../Components/MUITypography';
import { formatAmount } from '../../Util/UtilHelper';
import { align_row, VM_Colors, vmPageTitleSx, vmStatValueSx } from '../../Styles/VMStyles';

const RevenueChecker = ({ defaultFrom, defaultTo , userID ,subscriptionStatus}) => {
  // const theme = useTheme();
  const [startDate, setStartDate] = useState(defaultFrom || '');
  const [endDate, setEndDate] = useState(defaultTo || '');
  const [loading, setLoading] = useState(false);
  const [revenueData, setRevenueData] = useState({
    total: 0,
    rental: 0,
    food: 0,
    accommodation: 0,
    serviceCharges: 0,
  });
  const [error, setError] = useState(null);


  const sessionFactory = new StorageFactory('session');
  // const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';



  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // const fmt = useMemo(
  //   () =>
  //     new Intl.NumberFormat('en-IN', {
  //       style: 'currency',
  //       currency: 'INR',
  //       maximumFractionDigits: 0,
  //     }),
  //   []
  // );

  const fetchTotals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (startDate) params.from = startDate;
      if (endDate) params.to = endDate;
      if(userID) params.user_id = userID;

      const res = await axios.get(API_GET_INVOICES_TOTAL_COUNT, { params });
      let data = res?.data?.data ?? res?.data ?? {};
      if (data?.data) data = data.data;

      const rental = toNumber(data.total_venue_charges ?? data.venue_total);
      const food = toNumber(data.total_food_charges ?? data.food_total);
      const accommodation = toNumber(data.total_room_charges ?? data.room_total);
      const serviceCharges = toNumber(data.total_additional_services ?? data.additional_total);
      const total =
        toNumber(data.final_payable_amount ?? data.grand_total_amount) ||
        rental + food + accommodation + serviceCharges;

      setRevenueData({ total, rental, food, accommodation, serviceCharges });
    } catch (err) {
      console.error(err);
      setError('Failed to load data');
      setRevenueData({ total: 0, rental: 0, food: 0, accommodation: 0, serviceCharges: 0 });
    } finally {
      setLoading(false);
    }
  }, [endDate, startDate, userID]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  return (
    <Card
      sx={{
        boxShadow: 1,
        position: 'relative',
        borderRadius: 2,
        border: '1px solid #E0E0E0',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <CardContent
        sx={{
          p: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header with title and date pickers */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            gap: 2,
          }}
        >
           <MUITypography variant="pageTitle" sx={vmPageTitleSx}>Revenue Checker</MUITypography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="From"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Total Revenue */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 2,
            p: 2,
            mb: 3,
            textAlign: 'center',
            ...align_row,
            justifyContent:'center'
          }}
        >
          <MUITypography sx={{ fontSize:'16px', fontWeight: 500,mr:2 ,mt:0.3}} >Total Revenue Generated</MUITypography>
          <MUITypography variant="statValue" sx={vmStatValueSx}>
            {loading ? <Skeleton width={160} /> :  formatAmount(revenueData.total,currencySymbol) }
          </MUITypography>
          {error && (
            <MUITypography variant="caption" color="error">
              {error}
            </MUITypography>
          )}
        </Box>

        {/* Revenue Breakdown Cards */}
        <Grid container spacing={2} sx={{ flex: 1, alignContent: 'stretch' }}>
          {[
            { label: 'Rental Revenue', value: revenueData.rental, icon: <Home />, color: '#1976d2' },
            { label: 'Food', value: revenueData.food, icon: <Restaurant />, color: '#ef6c00' },
            { label: 'Accommodation', value: revenueData.accommodation, icon: <Hotel />, color: '#388e3c' },
            { label: 'Service Charges', value: revenueData.serviceCharges, icon: <Build />, color: '#757575' },
          ].map((item) => (
            <Grid item xs={12} sm={6} key={item.label} sx={{ display: 'flex' }}>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2.5,
                  flex: 1,
                  width: '100%',
                  minHeight: 88,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': { boxShadow: 3, transform: 'translateY(-3px)' },
                  bgcolor: 'background.paper',
                }}
              >
        <Avatar sx={{ bgcolor: item.color, width: 52, height: 52 }}>
          {item.icon}
        </Avatar>

        <Box sx={{ flexGrow: 1 }}>
          <MUITypography variant="subtitle2" sx={{fontSize:'14px', fontWeight: 600 ,color:VM_Colors.steel_gray}}>
            {item.label}
          </MUITypography>

          <Typography variant="statValue" sx={{ ...vmStatValueSx, mt: 0.6 }}>
            {loading ? <Skeleton width={80} /> : formatAmount(item.value,currencySymbol)}
          </Typography>
        </Box>
      </Box>
            </Grid>
          ))}
        </Grid>

      </CardContent>
    </Card>
  );
};

export default RevenueChecker;
